/**
 * Shared bulge-arc math (CURVES round — docs/bim-plan/11-curves-spike.md §1/§6 Phase A).
 * ONE derivation of center/radius/angles from the DXF bulge convention
 * (`bulge = tan(sweep/4)`, positive = CCW travel from edge start to edge end), replacing
 * the two previously-duplicated implementations (bim-geometry flatten.ts + dxf.ts —
 * whose center-side signs actually DISAGREED for non-semicircle arcs; flatten.ts was the
 * circle-verified one and is the convention here). Everything downstream — offsets,
 * snapping, corner welds, dimensions, the true-arc linework channel — derives through
 * this module, never a re-derivation.
 *
 * Sign conventions (verified against flattenPath's r=10 quarter-arc test):
 * - positive bulge = CCW sweep; the arc bows to the RIGHT of the travel direction
 *   (center sits on the LEFT).
 * - `sagPointOf` (the on-curve midpoint) = chordMid − leftNormal · (bulge · chord / 2).
 * - concentric LEFT offset by δ shrinks a CCW arc's radius (r' = r − sign(bulge)·δ).
 */
import type { Path2D, Pt2 } from './types'

export interface EdgeArc {
  c: Pt2
  r: number
  /** angle of the edge's START point about c (radians, atan2 convention) */
  a0: number
  /** a0 + sweep — the END point's angle, deliberately unnormalized */
  a1: number
  /** signed swept angle = 4·atan(bulge); positive = CCW travel */
  sweep: number
}

/** below this magnitude a bulge reads as straight (matches flattenPath's own guard) */
export const BULGE_EPS = 1e-12

/** 1/8" chord tolerance — the pipeline-wide tessellation default */
export const DEFAULT_ARC_TOL_FT = 1 / 96

const TWO_PI = Math.PI * 2

/** Resolve an edge's bulge to its true arc, or null for straight/degenerate edges. */
export function bulgeToArc(a: Pt2, b: Pt2, bulge: number | undefined | null): EdgeArc | null {
  const bg = bulge ?? 0
  if (Math.abs(bg) < BULGE_EPS) return null
  const [x1, y1] = a, [x2, y2] = b
  const chord = Math.hypot(x2 - x1, y2 - y1)
  if (chord < 1e-12) return null
  const sweep = 4 * Math.atan(bg)
  const radius = chord / (2 * Math.sin(Math.abs(sweep) / 2))
  // center: perpendicular from the chord midpoint (flatten.ts's verified math)
  const mx = (x1 + x2) / 2, my = (y1 + y2) / 2
  const h = Math.sqrt(Math.max(0, radius * radius - (chord / 2) ** 2))
    * Math.sign(bg) * (Math.abs(sweep) > Math.PI ? -1 : 1)
  const ux = -(y2 - y1) / chord, uy = (x2 - x1) / chord
  const c: Pt2 = [mx + ux * h, my + uy * h]
  const a0 = Math.atan2(y1 - c[1], x1 - c[0])
  return { c, r: radius, a0, a1: a0 + sweep, sweep }
}

/** The arc for edge `i` of a path (from nodes[i], wrapping when closed), or null. */
export function pathEdgeArc(path: Path2D, i: number): EdgeArc | null {
  const n = path.nodes.length
  const from = path.nodes[i]
  if (!from) return null
  const to = path.nodes[(i + 1) % n]
  if (!to || (!path.closed && i === n - 1)) return null
  return bulgeToArc(from.pt, to.pt, from.bulge)
}

/** true when any edge of the path carries a live bulge */
export function pathHasArcs(path: Path2D | null | undefined): boolean {
  if (!path?.nodes) return false
  const last = path.closed ? path.nodes.length : path.nodes.length - 1
  for (let i = 0; i < last; i++) {
    if (Math.abs(path.nodes[i].bulge ?? 0) >= BULGE_EPS) return true
  }
  return false
}

export const arcPointAtAngle = (arc: EdgeArc, ang: number): Pt2 =>
  [arc.c[0] + arc.r * Math.cos(ang), arc.c[1] + arc.r * Math.sin(ang)]

/** the arc's true (angle-bisected) midpoint — the sag point */
export const arcMidpoint = (arc: EdgeArc): Pt2 => arcPointAtAngle(arc, arc.a0 + arc.sweep / 2)

/** unit TRAVEL direction at angle `ang` (CCW arcs travel (−sin, cos); CW the negation) */
export function arcTangentAt(arc: EdgeArc, ang: number): Pt2 {
  const s = arc.sweep >= 0 ? 1 : -1
  return [-Math.sin(ang) * s, Math.cos(ang) * s]
}

export const angleOfPoint = (arc: EdgeArc, p: Pt2): number =>
  Math.atan2(p[1] - arc.c[1], p[0] - arc.c[0])

const norm2pi = (v: number): number => {
  let x = v % TWO_PI
  if (x < 0) x += TWO_PI
  return x
}

/** is `ang` within the swept range (a0 → a0+sweep, direction-aware)? `padRad` widens it. */
export function angleOnArc(arc: EdgeArc, ang: number, padRad = 1e-9): boolean {
  const d = arc.sweep >= 0 ? norm2pi(ang - arc.a0) : norm2pi(arc.a0 - ang)
  return d <= Math.abs(arc.sweep) + padRad || d >= TWO_PI - padRad
}

/** Radial projection of `p` onto the arc: nearest point ON the swept range, or null when
 *  the projection angle falls outside it. `dist` = |p − projection|. */
export function projectToArc(arc: EdgeArc, p: Pt2, padRad = 1e-9): { pt: Pt2; dist: number; ang: number } | null {
  const dx = p[0] - arc.c[0], dy = p[1] - arc.c[1]
  const len = Math.hypot(dx, dy)
  if (len < 1e-12) return null
  const ang = Math.atan2(dy, dx)
  if (!angleOnArc(arc, ang, padRad)) return null
  const pt: Pt2 = [arc.c[0] + (dx / len) * arc.r, arc.c[1] + (dy / len) * arc.r]
  return { pt, dist: Math.abs(len - arc.r), ang }
}

export const arcLengthOf = (arc: EdgeArc): number => arc.r * Math.abs(arc.sweep)

/** N/E/S/W quadrant points of the circle that actually lie on the swept arc. */
export function arcQuadrants(arc: EdgeArc): Pt2[] {
  const out: Pt2[] = []
  for (const ang of [0, Math.PI / 2, Math.PI, -Math.PI / 2]) {
    if (angleOnArc(arc, ang, 1e-6)) out.push(arcPointAtAngle(arc, ang))
  }
  return out
}

/**
 * Recompute a bulge for endpoints `a`→`b` lying on (or near) the circle at `c`,
 * preserving the ORIGINAL sweep direction — the post-trim/post-miter step of a
 * concentric offset (the trimmed arc keeps its center; only the swept range moved).
 */
export function bulgeFromEndpointsAboutCenter(a: Pt2, b: Pt2, c: Pt2, sweepSign: number): number {
  const angA = Math.atan2(a[1] - c[1], a[0] - c[0])
  const angB = Math.atan2(b[1] - c[1], b[0] - c[0])
  const sweep = (sweepSign >= 0 ? norm2pi(angB - angA) : -norm2pi(angA - angB))
  return Math.tan(sweep / 4)
}

/** The on-curve midpoint of edge a→b with `bulge` — exact, no arc solve needed. */
export function sagPointOf(a: Pt2, b: Pt2, bulge: number): Pt2 {
  const dx = b[0] - a[0], dy = b[1] - a[1]
  const chord = Math.hypot(dx, dy)
  const mid: Pt2 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]
  if (chord < 1e-12 || Math.abs(bulge) < BULGE_EPS) return mid
  const n: Pt2 = [-dy / chord, dx / chord]   // left normal of travel
  const h = (bulge * chord) / 2              // sag distance; bows RIGHT of travel for +bulge
  return [mid[0] - n[0] * h, mid[1] - n[1] * h]
}

/** Bulge for edge a→b whose curve should pass through `p` (the sag-drag inverse):
 *  h = signed left-normal distance of p from the chord; bulge = −2h/chord. */
export function bulgeFromSagPoint(a: Pt2, b: Pt2, p: Pt2): number {
  const dx = b[0] - a[0], dy = b[1] - a[1]
  const chord = Math.hypot(dx, dy)
  if (chord < 1e-12) return 0
  const n: Pt2 = [-dy / chord, dx / chord]
  const h = (p[0] - a[0]) * n[0] + (p[1] - a[1]) * n[1]
  return (-2 * h) / chord
}

/** |sag| for a bulge over a chord — trivially interconvertible, kept for readability. */
export const sagOfBulge = (bulge: number, chord: number): number => (bulge * chord) / 2

/**
 * Bulge whose MINOR arc over `chord` has radius `r` (radius entry always produces the
 * minor arc — a major arc from a radius alone is ambiguous). `sign` keeps the current
 * bow direction. r clamps up to chord/2 (the semicircle floor).
 */
export function bulgeForRadius(chord: number, r: number, sign: 1 | -1): number {
  if (chord < 1e-12 || r <= 0) return 0
  const rr = Math.max(r, chord / 2)
  const sweep = 2 * Math.asin(Math.min(1, chord / (2 * rr)))
  return Math.tan(sweep / 4) * sign
}

// ---------- intersections (offset miters, corner welds, guide × arc snaps) ----------

/** infinite line (p + t·d) × circle — 0, 1, or 2 points */
export function lineCircleIntersect(p: Pt2, d: Pt2, c: Pt2, r: number): Pt2[] {
  const dd = d[0] * d[0] + d[1] * d[1]
  if (dd < 1e-18) return []
  const fx = p[0] - c[0], fy = p[1] - c[1]
  const bq = 2 * (fx * d[0] + fy * d[1])
  const cq = fx * fx + fy * fy - r * r
  const disc = bq * bq - 4 * dd * cq
  if (disc < 0) return []
  const sq = Math.sqrt(disc)
  const t1 = (-bq - sq) / (2 * dd), t2 = (-bq + sq) / (2 * dd)
  const out: Pt2[] = [[p[0] + d[0] * t1, p[1] + d[1] * t1]]
  if (sq > 1e-12) out.push([p[0] + d[0] * t2, p[1] + d[1] * t2])
  return out
}

/** circle × circle — 0, 1, or 2 points (concentric/degenerate → none) */
export function circleCircleIntersect(c1: Pt2, r1: number, c2: Pt2, r2: number): Pt2[] {
  const dx = c2[0] - c1[0], dy = c2[1] - c1[1]
  const d = Math.hypot(dx, dy)
  if (d < 1e-12 || d > r1 + r2 || d < Math.abs(r1 - r2)) return []
  const a = (r1 * r1 - r2 * r2 + d * d) / (2 * d)
  const h2 = r1 * r1 - a * a
  const h = Math.sqrt(Math.max(0, h2))
  const mx = c1[0] + (dx / d) * a, my = c1[1] + (dy / d) * a
  const px = -(dy / d) * h, py = (dx / d) * h
  const out: Pt2[] = [[mx + px, my + py]]
  if (h > 1e-12) out.push([mx - px, my - py])
  return out
}

// ---------- tessellation (the canonical Path2D → polyline, hoisted from bim-geometry) ----------

/** chord-tolerance step count for sweeping `sweep` at radius `r` */
export function arcStepCount(r: number, sweep: number, tolFt: number): number {
  const maxStep = 2 * Math.acos(Math.max(-1, Math.min(1, 1 - tolFt / r)))
  return Math.max(2, Math.ceil(Math.abs(sweep) / Math.max(maxStep, 1e-3)))
}

/**
 * Path2D → straight-segment polyline; bulge arcs tessellate at chord tolerance. This IS
 * the pipeline's one tessellation (bim-geometry's flattenPath delegates here) — kernel
 * consumers that walk hosted positions by arc length (snap, rehost, handle configs) use
 * the SAME point list the solid generators extrude, so a station means one thing
 * everywhere. Bulge-free paths pass through as their bare node points (fast path).
 */
export function flattenPathPts(path: Path2D, tolFt = DEFAULT_ARC_TOL_FT): Pt2[] {
  const pts: Pt2[] = []
  const n = path.nodes.length
  const segCount = path.closed ? n : n - 1
  for (let i = 0; i < segCount; i++) {
    const a = path.nodes[i]
    const b = path.nodes[(i + 1) % n]
    if (i === 0) pts.push([a.pt[0], a.pt[1]])
    const arc = bulgeToArc(a.pt, b.pt, a.bulge)
    if (!arc) {
      if (Math.abs(a.bulge ?? 0) >= BULGE_EPS) continue   // degenerate chord — skip (legacy behavior)
      pts.push([b.pt[0], b.pt[1]])
      continue
    }
    const steps = arcStepCount(arc.r, arc.sweep, tolFt)
    for (let s = 1; s <= steps; s++) {
      const ang = arc.a0 + (arc.sweep * s) / steps
      pts.push(arcPointAtAngle(arc, ang))
    }
  }
  if (!path.closed && segCount < 1 && n === 1) pts.push([path.nodes[0].pt[0], path.nodes[0].pt[1]])
  return pts
}

/** Every live arc of a path — index = the edge's from-node index. */
export function pathArcs(path: Path2D): { index: number; arc: EdgeArc }[] {
  const out: { index: number; arc: EdgeArc }[] = []
  const last = path.closed ? path.nodes.length : path.nodes.length - 1
  for (let i = 0; i < last; i++) {
    const arc = pathEdgeArc(path, i)
    if (arc) out.push({ index: i, arc })
  }
  return out
}
